<template>
  <form
    class="SeasonFormView"
    :class="{
      'SeasonFormView--hasMultipleCrews': hasMultipleCrews,
      'SeasonFormView--isEditing': isEditing
    }"
    @submit.prevent="$emit('submit', payload)"
  >
    <fieldset
      class="SeasonFormView__fieldset"
      :disabled="isRequesting"
    >
      <SheetHeader
        v-bind="{ title }"
        @cancel="$emit('cancel')"
      />

      <div class="SeasonFormView__content">
        <Transition>
          <div
            v-if="!isRequesting"
            class="SeasonFormView__fieldGrid"
          >
            <FormField
              v-if="hasMultipleCrews && !isEditing"
              label="Crew"
              gridArea="crew"
            >
              <FormChecklist
                v-model="form.crewId"
                type="radio"
                name="crew"
                :options="crewOptions"
                isRequired
              />
            </FormField>

            <FormField
              gridArea="court"
              label="Court"
              isFieldset
            >
              <CourtCarouselSelect
                v-model="form.courtId"
                :courts="commissionerCourts"
              />
            </FormField>

            <FormField
              v-if="!isEditing"
              gridArea="date"
              label="Start date"
            >
              <DateInput
                v-model="form.date"
                name="date"
                isRequired
              />
            </FormField>

            <FormField
              gridArea="time"
              label="Time"
            >
              <TimeInput
                v-model="form.time"
                name="time"
                style="max-width: fit-content;"
                isRequired
              />
            </FormField>

            <FormField
              gridArea="duration"
              label="Duration"
            >
              <DurationRangeInput
                v-model="form.duration"
                v-bind="{startDate}"
              />
            </FormField>

            <FormField
              gridArea="minPlayerCount"
              label="Minimum numbers"
            >
              <MinPlayerCountControl
                v-model="form.minPlayerCount"
                data-testid="minPlayerCountControl"
              />
            </FormField>

            <FormField
              gridArea="maxPlayerCount"
              label="Player cap"
            >
              <MaxPlayerCountInput
                v-model="form.maxPlayerCount"
                :minPlayerCount="form.minPlayerCount"
              />
            </FormField>

            <FormField
              gridArea="rsvp"
              label="RSVP"
            >
              <RsvpRangeInput
                v-model="form.rsvpDayCount"
              />
            </FormField>
          </div>
        </Transition>
      </div>
    </fieldset>
  </form>
</template>

<script lang="ts" setup>
import CourtCarouselSelect from "@/components/CourtCarouselSelect.vue";
import DateInput from "@/components/DateInput.vue";
import DurationRangeInput from "@/components/DurationRangeInput.vue";
import { FormChecklistOption } from "@/components/FormChecklist.types";
import FormChecklist from "@/components/FormChecklist.vue";
import FormField from "@/components/FormField.vue";
import MaxPlayerCountInput from "@/components/MaxPlayerCountInput.vue";
import MinPlayerCountControl from "@/components/MinPlayerCountControl.vue";
import RsvpRangeInput from "@/components/RsvpRangeInput.vue";
import SheetHeader from "@/components/SheetHeader.vue";
import TimeInput from "@/components/TimeInput.vue";
import { supabaseClient } from "@/lib/database/supabase";
import useCommissionerCourtStore from "@/stores/useCommissionerCourtStore";
import useCurrentPlayerStore from "@/stores/useCurrentPlayerStore";
import { TZDate } from "@date-fns/tz";
import { Season } from "@shared/lib/database/query.types";
import { addDays, addHours, format, set } from "date-fns";
import { storeToRefs } from "pinia";
import { computed, onBeforeMount, ref } from "vue";
import { SeasonFormPayload } from "./SeasonFormView.types";

const isRequesting = defineModel<boolean>("isRequesting", {
  default: true,
});

const emit = defineEmits<{
  cancel: [],
  submit: [SeasonFormPayload | undefined],
}>();

const {
  seasonId,
} = defineProps<{
  seasonId?: string,
  title: string,
}>();

const {
  commissionerCrews,
} = storeToRefs(useCurrentPlayerStore());

const {
  fetchCrewMemberships,
} = useCurrentPlayerStore();

const {
  commissionerCourts,
} = storeToRefs(useCommissionerCourtStore());

const {
  fetchCommissionerCourts,
} = useCommissionerCourtStore();

const existingSeason = ref<Season | null>(null);
const isEditing = computed(() => !!seasonId);

const form = ref<{
  crewId: string | null,
  courtId: string | null,
  date: string,
  time: string,
  duration: number,
  minPlayerCount: number | null,
  maxPlayerCount: number | null,
  rsvpDayCount: number | null,
}>(getInitialForm());

onBeforeMount(async () => {
  if (seasonId) {
    await fetchExistingSeason(seasonId);
    form.value = getInitialForm();
  }

  try {
    await Promise.all([fetchCommissionerCrews(), fetchCourts()]);
  } catch (err) {
    alert((err as Error).message);
    emit("cancel");
  }

  isRequesting.value = false;
});

const hasMultipleCrews = computed(() => commissionerCrews.value.length > 1);

const court = computed(() => commissionerCourts.value.find((court) => court.id === form.value.courtId));

const startDate = computed(() => {
  if (!court.value || !form.value.date || !form.value.time) return;

  const [
    year,
    month,
    day,
  ] = form.value.date.split("-").map(parseFloat);
  const [hours, minutes] = form.value.time.split(":").map(parseFloat);

  return new TZDate(year, month - 1, day, hours, minutes, court.value.timezone);
});

const endDate = computed(() => {
  if (!startDate.value || !form.value.duration) return;

  return addHours(startDate.value, form.value.duration);
});

const payload = computed((): SeasonFormPayload | undefined => {
  if (!form.value.crewId || !form.value.courtId || !startDate.value || !endDate.value) return;

  return {
    season: {
      crew_id: form.value.crewId,
      default_court_id: form.value.courtId,
      default_start_time: `${form.value.time}:00`,
      default_end_time: format(endDate.value, "HH:mm:ss"),
      default_min_player_count: form.value.minPlayerCount,
      default_max_player_count: form.value.maxPlayerCount,
      default_rsvp_day_count: form.value.rsvpDayCount,
    },
    run: {
      court_id: form.value.courtId,
      start_at: startDate.value.toISOString(),
      end_at: endDate.value.toISOString(),
      crew_ids: [form.value.crewId],
      min_player_count: form.value.minPlayerCount || undefined,
      max_player_count: form.value.maxPlayerCount || undefined,
      rsvp_day_count: form.value.rsvpDayCount || undefined,
    },
  };
});

const crewOptions = computed((): Array<FormChecklistOption<string | null>> => {
  return commissionerCrews.value.map((crew, i) => ({
    name: `crew${1 + i}`,
    label: crew.name,
    value: crew.id,
  }));
});

function getInitialForm () {
  const defaultStartDate = addDays(set(new Date(), {
    hours: 8,
    minutes: 0,
    seconds: 0,
  }), 2);

  return {
    crewId: getInitialCrewId(),
    courtId: existingSeason.value?.default_court_id || null,
    date: existingSeason.value?.start_on || format(defaultStartDate, "yyyy-MM-dd"),
    time: existingSeason.value?.default_start_time?.slice(0, 5) || format(defaultStartDate, "HH:mm"),
    duration: getSeasonDuration(existingSeason.value) ?? 2,
    minPlayerCount: existingSeason.value?.default_min_player_count ?? null,
    maxPlayerCount: existingSeason.value?.default_max_player_count ?? null,
    rsvpDayCount: existingSeason.value ? existingSeason.value.default_rsvp_day_count : 3,
  };
}

function getSeasonDuration (season: Season | null) {
  if (!season?.default_start_time || !season.default_end_time) return null;

  const [startHour, startMinute] = season.default_start_time.split(":").map(Number);
  const [endHour, endMinute] = season.default_end_time.split(":").map(Number);

  return ((endHour * 60 + endMinute) - (startHour * 60 + startMinute)) / 60;
}

function getInitialCrewId () {
  return existingSeason.value?.crew_id || commissionerCrews.value[0]?.id || null;
}

async function fetchExistingSeason (seasonId: string) {
  const {
    data: season,
    error,
  } = await supabaseClient
    .from("seasons")
    .select("*")
    .eq("id", seasonId)
    .maybeSingle();

  if (season) {
    existingSeason.value = season;
  } else if (error) {
    alert(error.message);
  }
}

async function fetchCommissionerCrews () {
  await fetchCrewMemberships();

  form.value.crewId = getInitialCrewId();
}

async function fetchCourts () {
  await fetchCommissionerCourts();

  form.value.courtId = commissionerCourts.value.find((court) => {
    return (existingSeason.value?.default_court_id ?? form.value.courtId) === court.id;
  })?.id || commissionerCourts.value.at(0)?.id || null;
}
</script>

<style lang="scss" scoped>
.SeasonFormView {
  position: absolute;
  inset: 0;
}

.SeasonFormView__fieldset {
  display: grid;
  align-content: start;
  height: 100%;
}

.SeasonFormView__content {
  position: relative;

  min-height: 0;
  padding-bottom: var(--safeAreaInsetBottom);
  overflow-y: auto;
}

.SeasonFormView__fieldGrid {
  display: grid;
  grid-template-areas: "court court"
    "date time"
    "duration duration"
    "minPlayerCount minPlayerCount"
    "maxPlayerCount maxPlayerCount"
    "rsvp rsvp";
  grid-template-columns: 1fr auto;
  gap: 24px 16px;
  padding: 24px 16px;

  transition: 350ms opacity;

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }

  .SeasonFormView--hasMultipleCrews & {
    grid-template-areas: "crew crew"
      "court court"
      "date time"
      "duration duration"
      "minPlayerCount minPlayerCount"
      "maxPlayerCount maxPlayerCount"
      "rsvp rsvp";
  }

  .SeasonFormView--isEditing & {
    grid-template-areas: "court court"
      "time ."
      "duration duration"
      "minPlayerCount minPlayerCount"
      "maxPlayerCount maxPlayerCount"
      "rsvp rsvp";
  }
}
</style>
